import { useApiErrorSnackbar } from '../common/useApiErrorSnackbar';
import { virtualIbanCountries } from '~/constants';
import { addSddMandatesDataQa } from '~/constants/commons/data-qa';
import { useWalletSDDMandatStore } from '~/stores/wallets/wallet-sdd-mandat-store';
import useWalletsApi from '~/composables/api/useWalletsApi';
import { useWalletsStore } from '~/stores/wallets/wallets-store';
import type {
  DisableBodyType,
  DropdownButtonType,
  EnableBodyType,
  PaymentFormInfosType,
  RequestDebitType,
  SavedCardsInfosType,
  SddMandateInfosType,
  SnackbarMessage,
  VirtualIbanBody,
  VirtualIBANInfosType,
  VirtualIbanResponse,
  FetchError
} from '~/types';

export const useWalletPayment = () => {
  const { $i18n } = useNuxtApp();
  const { t, locale } = $i18n;
  const {
    postSDDMandat,
    postVirtualIban,
    getVirtualIbansDocument,
    putDisable,
    putEnable,
    putDisableVirtualIban
  } = useWalletsApi();
  const {
    wallet,
    language,
    openAddSddMandateModal,
    holder,
    iban,
    bic,
    password,
    openDisableSddMandateModal,
    addSDDMandatStep
  } = storeToRefs(useWalletSDDMandatStore());
  const {
    userInfos,
    openAddVirtualIbanModal,
    virtualIBANBody,
    virtualIBANInfos,
    openDisableModal,
    openEnableModal,
    openViewPaymentFormModal,
    openRequestDebitModal,
    payementFormInfos,
    sddMandateInfos,
    savedCardsInfos,
    requestDebitInfos,
    disablePassword,
    enablePassword,
    redirectUrl
  } = storeToRefs(useWalletsStore());
  const { pdfName, src, viewPdf } = storeToRefs(usePdfViewerStore());
  const { paymentFormId } = storeToRefs(useAddPaymentStore());
  const { add } = useSnackbar();
  const { notifyApiError } = useApiErrorSnackbar();

  const toggleDisableModal = () => {
    openDisableModal.value = !openDisableModal.value;
  };

  const toggleEnableModal = () => {
    openEnableModal.value = !openEnableModal.value;
  };

  const toggleOpenDisablePaymentFormModal = (payementInfos: PaymentFormInfosType) => {
    payementFormInfos.value = payementInfos;
    toggleDisableModal();
  };

  const toggleOpenDisableSddMandateModal = (sddMandateData: SddMandateInfosType) => {
    sddMandateInfos.value = sddMandateData;
    toggleDisableModal();
  };

  const toggleOpenDisableSavedCardsModal = (savedCardsData: SavedCardsInfosType) => {
    savedCardsInfos.value = savedCardsData;
    toggleDisableModal();
  };

  const handleDisable = async (type: string, id: string, emit: Function) => {
    try {
      const translations: { [key: string]: string } = {
        ibans: 'bankAccounts',
        savedCard: 'paymentMethods.savedCards.modals.disableModal',
        sddmandates: 'paymentMethods.sddMandates.modals.disableModal',
        paymentforms: 'paymentMethods.paymentForms.modals.disableModal',
        virtualIbans: 'paymentMethods.virtualIbans.list'
      };
      const body: DisableBodyType = {
        Password: disablePassword.value.value,
        Language: locale.value
      };
      await putDisable(type === 'virtualIbans' ? 'ibans' : type, id, body);
      emit();
      add({
        type: 'warning',
        message: t(`wallets.${translations[type]}.successDisable`, { id })
      } as SnackbarMessage);
      toggleDisableModal();
    } catch (error: unknown) {
      notifyApiError(error);
    }
  };

  const handleDisableVirtualIban = async (id: number, body: VirtualIbanBody) => {
    try {
      await putDisableVirtualIban(id, body);
      add({
        type: 'success',
        message: t('wallet.paymentMethods.virtualIbans.list.disabled', { id })
      } as SnackbarMessage);
      toggleDisableModal();
    } catch (error: unknown) {
      notifyApiError(error);
    }
  };

  const handleEnable = async (type: string, id: string, emit: Function) => {
    try {
      const translations: { [key: string]: string } = {
        sddmandates: 'paymentMethods.sddMandates.modals.enableModal'
      };
      const body: EnableBodyType = {
        Password: enablePassword.value.value,
        Language: locale.value,
        NewStatus: 5
      };
      await putEnable(type, id, body);
      emit();
      add({
        type: 'warning',
        message: t(`wallets.${translations[type]}.successEnable`, { id })
      } as SnackbarMessage);
      toggleEnableModal();
    } catch (error: unknown) {
      notifyApiError(error);
    }
  };

  const toggleOpenViewPaymentForm = (payementInfos: PaymentFormInfosType) => {
    if (!openViewPaymentFormModal.value) {
      payementFormInfos.value = payementInfos;
    }
    paymentFormId.value = '';
    openViewPaymentFormModal.value = !openViewPaymentFormModal.value;
  };

  const toggleRequestDebit = () => {
    openRequestDebitModal.value = !openRequestDebitModal.value;
  };

  const toggleRequestDebitModal = (requestDebitData: RequestDebitType) => {
    requestDebitInfos.value = requestDebitData;
    toggleRequestDebit();
  };

  const handleTextCopy = () => ({
    label: t('wallets.paymentMethods.paymentForms.details.paymentFormURL'),
    value: redirectUrl.value,
    size: 'tiny'
  });

  const virtualIbanStep = ref(0);
  const virtualIbanBody = ref({
    Country: undefined as undefined | string,
    Password: { value: '' }
  });
  const vIbanData = ref<VirtualIbanResponse | null>(null);

  const addSDDMandatTimeline = computed(() => [
    {
      title: t('common.mandat'),
      status: handleAddSDDMandatTimelineStatus('mandat'),
      dataQa: addSddMandatesDataQa?.mandateRadio
    },
    {
      title: t('common.additionalInfos'),
      status: handleAddSDDMandatTimelineStatus('moreInfos'),
      dataQa: addSddMandatesDataQa?.additionalInformationRadio
    }
  ]);

  const addSDDstepTitles = computed(() => [
    t('wallets.paymentMethods.sddMandates.modals.add.firstStep'),
    t('wallets.paymentMethods.sddMandates.modals.add.secondStep')
  ]);

  const addSDDmandatOptions = computed(() => [
    {
      text: t('wallets.paymentMethods.sddMandates.modals.add.recurringMandat'),
      value: 'recurring',
      disabled: false,
      dataQa: addSddMandatesDataQa?.recuring
    },
    {
      text: t('wallets.paymentMethods.sddMandates.modals.add.b2bMandat'),
      value: 'b2b',
      disabled: false,
      dataQa: addSddMandatesDataQa?.b2b
    }
  ]);

  const handleAddSDDMandatTimelineStatus = (step: string) => {
    switch (step) {
      case 'mandat':
        return addSDDMandatStep.value === 0 ? 'progress' : 'done';
      case 'moreInfos':
        return addSDDMandatStep.value === 1 ? 'progress' : 'todo';
    }
  };

  const handleSubmitAddSddMandate = async () => {
    if (addSDDMandatStep.value === 0) {
      addSDDMandatStep.value++;
    } else {
      try {
        wallet.value = userInfos.value.wallet;
        language.value = locale.value;
        const data = await postSDDMandat();
        add({
          type: 'success',
          message: t('wallets.paymentMethods.sddMandates.modals.add.successAdd', { id: data.Id })
        } as SnackbarMessage);
        return 1;
      } catch (error: unknown) {
        notifyApiError(error);
      }
    }
  };

  const toggleOpenAddSddMandateModal = () => {
    openAddSddMandateModal.value = !openAddSddMandateModal.value;
  };

  // Toggles the SDD mandate store's own disable-confirm modal (separate from the generic disable modal)
  const toggleSddMandateStoreDisableModal = () => {
    openDisableSddMandateModal.value = !openDisableSddMandateModal.value;
  };

  const validateAddSDDMandat = () => {
    switch (addSDDMandatStep.value) {
      case 0:
        return holder.value.isValid && iban.value.isValid && bic.value.isValid;
      case 1:
        return password.value.value;
      default:
        return false;
    }
  };

  const handleShowAddSDDMandatSections = (section: number) => section === addSDDMandatStep.value;

  const loadingPdfKeys = ref(new Set<string>());

  const loadVirtualIBANPDF = async (language: string, virtualIBANId: string) => {
    const key = `${virtualIBANId}_${language}`;
    if (loadingPdfKeys.value.has(key)) return;
    loadingPdfKeys.value = new Set(loadingPdfKeys.value).add(key);
    try {
      const partnerName = localStorage.getItem('partnerName');
      const data = await getVirtualIbansDocument(language, virtualIBANId);
      src.value = URL.createObjectURL(new Blob([data]));
      pdfName.value = `virtual-iban_${partnerName}_${userInfos.value.intId}_${virtualIBANId}_${language}.pdf`;
      viewPdf.value = true;
    } catch (error: unknown) {
      const errorResponse = error as FetchError;
      const decoder = new TextDecoder('utf-8');
      const dataString = decoder.decode(errorResponse.response?.data);
      notifyApiError(error, { text: JSON.parse(dataString) });
    } finally {
      const next = new Set(loadingPdfKeys.value);
      next.delete(key);
      loadingPdfKeys.value = next;
    }
  };

  const isAnyPdfLoading = (virtualIBANId?: string) => {
    if (!virtualIBANId) return false;
    return [...loadingPdfKeys.value].some((k) => k.startsWith(`${virtualIBANId}_`));
  };

  const toggleAddVirtualIban = () => {
    openAddVirtualIbanModal.value = !openAddVirtualIbanModal.value;
  };

  const toggleDisableVIban = (virtualIbanInfos: VirtualIBANInfosType) => {
    virtualIBANInfos.value = virtualIbanInfos;
    toggleDisableModal();
  };

  const handlegenerateVirtualIban = async (emit: Function) => {
    try {
      virtualIBANBody.value.password = virtualIbanBody.value.Password.value;
      virtualIBANBody.value.Country = virtualIbanBody.value.Country;
      virtualIBANBody.value.language = locale.value;
      virtualIBANBody.value.Wallet = userInfos.value.wallet;
      const data = await postVirtualIban();
      vIbanData.value = data;
      add({
        type: 'success',
        message: t('wallets.paymentMethods.virtualIbans.list.successgenerated', {
          id: vIbanData.value?.Id
        })
      } as SnackbarMessage);
      if (virtualIbanStep.value === 0) {
        virtualIbanStep.value++;
      }
      emit();
    } catch (error: unknown) {
      notifyApiError(error);
    }
  };

  const exportPdfOptions = (
    country: string | undefined,
    virtualIbansId: string | undefined
  ): DropdownButtonType[] => {
    if (country && virtualIbansId) {
      const languages = [locale.value];
      const countryLanguage =
        virtualIbanCountries[country.toLowerCase() as keyof typeof virtualIbanCountries].value;
      if (isSupportedLocale(countryLanguage) && !languages.includes(countryLanguage)) {
        languages.push(countryLanguage);
      } else {
        languages.push('en');
      }
      return languages.map((language: string) => ({
        iconPosition: 'icon-only',
        leftIcon: language,
        action: () => loadVirtualIBANPDF(language, virtualIbansId),
        theme: 'ghost',
        permission: true,
        isLoading: loadingPdfKeys.value.has(`${virtualIbansId}_${language}`),
        isDisabled: isAnyPdfLoading(virtualIbansId)
      }));
    }
    return [];
  };

  return {
    toggleDisableModal,
    toggleEnableModal,
    toggleOpenDisablePaymentFormModal,
    toggleOpenDisableSddMandateModal,
    toggleOpenDisableSavedCardsModal,
    handleDisable,
    handleDisableVirtualIban,
    handleEnable,
    toggleOpenViewPaymentForm,
    toggleRequestDebit,
    toggleRequestDebitModal,
    handleTextCopy,
    addSDDMandatTimeline,
    addSDDstepTitles,
    addSDDmandatOptions,
    handleSubmitAddSddMandate,
    toggleOpenAddSddMandateModal,
    validateAddSDDMandat,
    handleShowAddSDDMandatSections,
    toggleSddMandateStoreDisableModal,
    virtualIbanStep,
    virtualIbanBody,
    vIbanData,
    loadVirtualIBANPDF,
    toggleAddVirtualIban,
    toggleDisableVIban,
    handlegenerateVirtualIban,
    isAnyPdfLoading,
    exportPdfOptions
  };
};
